import { getTargetSystem } from "@Exports/util/exportType";
import { ExportProgressState } from "@medapro-types/Exports/components/ProgressExport";
import { ExportType, LayoutFileType } from "@medapro-types/Exports/export-enum";
import api from "./api";
import endpoints from "./endpoints";
import { List } from "lodash";
import { Guid } from "@/util/guid";
import { TradeProExportUploadOptions } from "@medapro-types/Exports/components/common/Upload";
import { ILocalizedRecord } from "@medapro-types/common/components/localizedDataListEditorTypes";
import { ITranslationTableTypeModel } from "@medapro-types/Exports/components/FastPage/ITranslationTableTypeModel";

export default {
  getCatalogs: async function (language: string, exportType: ExportType) {
    const response = await api.get(endpoints.exports.getCatalogs, { params: { language, targetSystem: getTargetSystem(exportType) } });
    return response.data;
  },
  getCatalogMappingProjects: async function (catalogId: string, exportType: ExportType) {
    const response = await api.get(endpoints.exports.getCatalogMappingProjects, { params: { catalogId, targetSystem: getTargetSystem(exportType) } });
    return response.data;
  },
  getArticleFilters: async function () {
    const response = await api.get(endpoints.exports.getArticleFilters, { params: {} });
    return response.data;
  },
  getExcelMacros: async function () {
    const response = await api.get(endpoints.exports.getExcelMacros, { params: {} });
    return response.data;
  },
  getRuleProfiles: async function (mappingId: string) {
    const response = await api.get(endpoints.exports.getRuleProfiles, { params: { catalogMappingTargetId: mappingId } });
    return response.data;
  },
  startExport: async function (exportId: string) {
    const response = await api.post(endpoints.exports.startExport, {}, { params: { exportId: exportId } });
    return response.data;
  },
  getProgressInfo: async function (language: string, exportRunId: string): Promise<ExportProgressState> {
    const response = await api.get(endpoints.exports.getExportProgress, { params: { exportRunId: exportRunId, language: language } });
    return response.data;
  },
  getExportSettings: async function (id: string) {
    /*  const response = await api.get(endpoints.exports.getExportSettings, { params: { id: id } });
    return response.data; */
    const data: Record<string, string> = {};
    const keys = Object.keys(exampleSettings[id]);
    for (let i = 0; i < 10; i++) {
      for (const key of keys) {
        data[key + i] = exampleSettings[id][key];
      }
    }

    return data;
  },
  delete: async function (exportIds: List<Guid>) {
    const response = await api.post(endpoints.exports.delete, exportIds, {
      params: {}
    });
    return response.data;
  },
  getExports: async function (exportType: ExportType, language: string) {
    const response = await api.get(endpoints.exports.getExports, { params: { targetSystem: getTargetSystem(exportType), language } });
    return response.data;
  },
  createExport: async function (args: unknown) {
    const response = await api.post(endpoints.exports.create, args);
    return response.data;
  },
  updateExport: async function (args: unknown) {
    const response = await api.post(endpoints.exports.update, args);
    return response.data;
  },
  cancelExport: async function (exportRunId: string) {
    const response = await api.post(endpoints.exports.cancel, {}, { params: { exportRunId: exportRunId } });
    return response.data;
  },
  uploadXsltFile: async function (file: File | null) {
    const response = await api.post(
      endpoints.exports.uploadXsltFile,
      { file },
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );
    return response.data;
  },
  getLastExport: async function (exportId?: string) {
    const response = await api.get(endpoints.exports.getLastExport, { params: { exportId } });
    return response.data;
  },
  isLastExportEnabled: async function (exportId?: string) {
    if (!exportId) return false;
    const response = await api.get(endpoints.exports.enableLastExport, { params: { exportId } });
    return response.data;
  },
  validateFTP: async function (config: TradeProExportUploadOptions, exportId: string | string[]) {
    const response = await api.post(endpoints.exports.tradePro.validateFTP, config, { params: { exportId } });
    return response.data;
  },
  prepareForDownload: async function (exportRunId: string) {
    const response = await api.post(endpoints.exports.prepareForDownload, {}, { params: { exportRunId: exportRunId } });
    return response.data;
  },
  getTranslationTable(languages: string[]) {
    return api
      .get(endpoints.exports.getTranslationTable, {
        params: { languages }
      })
      .then((x) => Promise.resolve(x.data));
  },
  updateTranslationTable: async function (model: ILocalizedRecord<ITranslationTableTypeModel>[]) {
    const response = await api.post(endpoints.exports.updateTranslationTable, model).then((x) => Promise.resolve(x.data));
    return response.data;
  },
  createFastPageLayout: async function (uiName: string, description: string, projectType: LayoutFileType | string) {
    const response = await api.post(endpoints.fastpage.designer.create, {
      uiName,
      description,
      projectType
    });
    return response.data;
  },
  renameFastPageLayout: async function (itemId: string, newName: string, description: string) {
    const response = await api.post(endpoints.fastpage.designer.rename, { itemId, newName, description });
    return response.data;
  },
  duplicateFastPageLayout: async function (itemId: string, newName: string, description: string, basedOnItem: string | null) {
    const response = await api.post(endpoints.fastpage.designer.paste, { itemId, newName, description, basedOnItem });
    return response.data;
  },
  getFastPageLayoutInfo: async function (layoutIds: Array<string>) {
    const response = await api.post(endpoints.fastpage.designer.getInfo, layoutIds);
    return response.data;
  },
  getFastPageItemsUnderFolder: async function (folderId: string | null) {
    const response = await api.get(endpoints.fastpage.designer.getItemsFromFolder, {
      params: { folderId }
    });
    return response.data;
  },
  getFastPageUserOrDefaultFolders: async function (folderName: string) {
    const response = await api.get(endpoints.fastpage.designer.getUserOrDefaultFolders, {
      params: { folderName }
    });
    return response.data;
  },
  createPreviewFile: async function (layoutId: string, exportId: string) {
    const response = await api.get(endpoints.fastpage.designer.createPreview, {
      params: { layoutId, exportId }
    });
    return response.data;
  },
  validateAndGetTradeProChannels: async function (exportId: string | null, args: any) {
    const response = await api.post(endpoints.exports.tradePro.validateAndGetTradeProChannels, args, { params: { exportId } });
    return response.data;
  },
  getDesktopDesigner: async function (exportId: string, reportRepositoryID: string | null) {
    const response = await api.get(endpoints.fastpage.windowsDesigner.start, {
      params: { exportId, reportRepositoryID }
    });
    return response.data;
  }
};

const exampleSettings: Record<string, Record<string, string>> = {
  "65da6eee-5dc7-4d64-825a-e14ed67265f0": {
    id: "65da6eee-5dc7-4d64-825a-e14ed67265f0",
    catalog: "Nec Leo Foundation",
    mapping: "Suspendisse Aliquet Company",
    language: "Netherlands",
    region: "Maryland",
    ruleProfile: "Hyacinth Carney",
    exportFormat: "Json",
    marcoTemplate: "Mason Walters",
    addFilter: "Raya Wynn"
  },
  "7CF89715-BEBB-2D6D-B89B-D958E517E259": {
    id: "7CF89715-BEBB-2D6D-B89B-D958E517E259",
    catalog: "Non Luctus Institute",
    mapping: "In Aliquet Industries",
    language: "Brazil",
    region: "Tasmania",
    ruleProfile: "Macy Yang",
    exportFormat: "XML",
    marcoTemplate: "Rinah Hopkins",
    addFilter: "Lucas Mitchell"
  },
  "355D4553-B595-3425-B493-3B67AC872466": {
    id: "355D4553-B595-3425-B493-3B67AC872466",
    catalog: "Pellentesque Eget Dictum Foundation",
    mapping: "Ipsum Dolor Corp.",
    language: "Costa Rica",
    region: "Lombardia",
    ruleProfile: "Clarke Bender",
    exportFormat: "Pro",
    marcoTemplate: "Stuart Byrd",
    addFilter: "Jesse Ortega"
  }
};
